// src/components/admin/categories/CategoryDialog.tsx
import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Upload } from 'lucide-react'; // Importar Loader2 e Upload

// Importar funções de categoria e upload
import { createCategory, updateCategory } from '@/lib/categories';
import { uploadFile } from '@/integrations/supabase/storage'; // Importar uploadFile
import type { Category, CategoryFormData } from '@/types/category';
import ImageHandler from '@/components/ImageHandler'; // Importar ImageHandler

interface CategoryDialogProps {
    isOpen: boolean;
    mode: 'addCategory' | 'editCategory' | null;
    itemToEdit?: Category | null;
    onClose: () => void;
    onSuccess: () => void;
}

const defaultCategoryData: CategoryFormData = {
    name: '',
    description: '',
    status: 'active',
    image_url: '', // Mantém image_url no formData para preview e valor existente
};

export function CategoryDialog({ isOpen, mode, itemToEdit, onClose, onSuccess }: CategoryDialogProps) {
    const [formData, setFormData] = useState<CategoryFormData | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null); // Estado para o arquivo de imagem
    const [imagePreview, setImagePreview] = useState<string | null>(null); // Estado para a prévia da imagem local
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setError(null);
        setImageFile(null); // Resetar arquivo ao abrir/mudar modo
        if (mode === 'editCategory' && itemToEdit) {
            const initialData = {
                name: itemToEdit.name,
                description: itemToEdit.description || '',
                status: itemToEdit.status,
                image_url: itemToEdit.imageUrl || '', // Usar imageUrl existente
            };
            setFormData(initialData);
            setImagePreview(itemToEdit.imageUrl || null); // Mostrar imagem existente na prévia
        } else if (mode === 'addCategory') {
            setFormData(defaultCategoryData);
            setImagePreview(null); // Limpar prévia para nova categoria
        } else {
            setFormData(null);
            setImagePreview(null);
        }
    }, [isOpen, mode, itemToEdit]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData(prev => prev ? { ...prev, status: checked ? 'active' : 'inactive' } : null);
    };

    // Handler para mudança de imagem
    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file); // Armazenar o arquivo
            // Gerar prévia local
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            // Se o usuário cancelar a seleção, resetar arquivo e prévia (ou voltar à imagem original se editando)
            setImageFile(null);
             setImagePreview(mode === 'editCategory' && itemToEdit ? itemToEdit.imageUrl || null : null);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!formData || isLoading) return;

        setIsLoading(true);
        setError(null);

        let finalImageUrl = formData.image_url || null; // Começa com a URL existente ou null

        try {
            // 1. Fazer upload da nova imagem, se houver
            if (imageFile) {
                toast({ title: 'Upload', description: 'Enviando imagem da categoria...' });
                finalImageUrl = await uploadFile(imageFile, 'categories'); // Assume bucket 'categories'
                toast({ title: 'Upload Concluído', description: 'Imagem enviada com sucesso.' });
                // console.log("Uploaded category image:", finalImageUrl);
            }

            // 2. Preparar dados para API
             const dataToSend: Partial<CategoryFormData> = {
                 name: formData.name,
                 description: formData.description,
                 status: formData.status,
                 image_url: finalImageUrl, // Usa a URL do upload ou a existente/null
             };

             // 3. Chamar API
            let result;
            if (mode === 'addCategory') {
                result = await createCategory(dataToSend as CategoryFormData);
            } else if (mode === 'editCategory' && itemToEdit) {
                result = await updateCategory(itemToEdit.id, dataToSend);
            } else {
                 throw new Error("Modo inválido ou item ausente para submissão.");
            }

            if (result && result.error) {
                 throw new Error(result.error);
             }

            toast({ title: 'Sucesso!', description: `Categoria ${mode?.includes('add') ? 'criada' : 'atualizada'} com sucesso.` });
            onSuccess(); // Chama o callback de sucesso (que deve fechar o diálogo e invalidar queries)

        } catch (err: any) {
            // console.error("Falha na operação da categoria:", err);
            const errorMessage = err.message || 'Ocorreu um erro desconhecido.';
            setError(errorMessage);
            toast({ title: 'Erro', description: errorMessage, variant: 'destructive' });
        } finally {
            setIsLoading(false);
             // Não resetar imageFile aqui, pois onSuccess deve fechar o diálogo, e useEffect lida com reset
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    }

    let title = '';
    if (mode === 'addCategory') title = 'Adicionar Nova Categoria';
    if (mode === 'editCategory') title = 'Editar Categoria';

    return (
        // Usar onOpenChange para resetar estado ao fechar externamente
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-[480px]"> {/* Aumentar um pouco a largura */}
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                {formData ? (
                    <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                        {/* Nome */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Nome
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="col-span-3"
                                required
                                disabled={isLoading}
                            />
                        </div>
                        {/* Descrição */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="description" className="text-right">
                                Descrição
                            </Label>
                            <Textarea
                                id="description"
                                name="description"
                                value={formData.description || ''}
                                onChange={handleInputChange}
                                className="col-span-3"
                                disabled={isLoading}
                            />
                        </div>

                        {/* Upload de Imagem */}
                        <div className="grid grid-cols-4 items-start gap-4"> {/* items-start para alinhar label no topo */}
                            <Label htmlFor="image-upload" className="text-right pt-2"> {/* pt-2 para alinhar melhor */}
                                Imagem
                            </Label>
                            <div className="col-span-3 space-y-2">
                                <Input
                                    id="image-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                                    disabled={isLoading}
                                />
                                {imagePreview && (
                                    <div className="mt-2 border rounded-md overflow-hidden max-w-[150px]"> {/* Preview menor */}
                                        <ImageHandler
                                            src={imagePreview}
                                            alt="Prévia da imagem da categoria"
                                            className="w-full h-auto object-contain"
                                        />
                                    </div>
                                )}
                                {!imagePreview && mode === 'editCategory' && (
                                     <p className="text-xs text-muted-foreground">Nenhuma imagem selecionada ou associada.</p>
                                )}
                                 {!imagePreview && mode === 'addCategory' && (
                                     <p className="text-xs text-muted-foreground">Selecione uma imagem para upload.</p>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="status" className="text-right">
                                Status
                            </Label>
                            <div className="col-span-3 flex items-center space-x-2">
                                 <Switch
                                     id="status"
                                     checked={formData.status === 'active'}
                                     onCheckedChange={handleSwitchChange}
                                     disabled={isLoading}
                                 />
                                 <span>{formData.status === 'active' ? 'Ativo' : 'Inativo'}</span>
                            </div>
                        </div>
                        {error && <p className="text-red-500 text-sm col-span-4 text-center">{error}</p>}
                        <DialogFooter>
                             <DialogClose asChild>
                                <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancelar</Button>
                             </DialogClose>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Salvando...
                                    </>
                                ) : (
                                    'Salvar'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                 ) : (
                    <div className="py-4 text-center">Carregando formulário...</div>
                 )}
            </DialogContent>
        </Dialog>
    );
}
